import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CloseOutlined, UserAddOutlined } from "@ant-design/icons";
import { Flex, Form, message } from "antd";

import { createUser } from "../../slice";
import { selectCreate } from "../../selector";
import type {
  AdminUserRole,
  AdminUserStatus,
  CreateUserPayload,
} from "../../types";
import {
  AddUserFieldGrid,
  AddUserInput,
  AddUserModalBody,
  AddUserModalClose,
  AddUserModalDivider,
  AddUserModalFooter,
  AddUserModalHeader,
  AddUserModalHeaderIcon,
  AddUserModalStyled,
  AddUserModalSubtitle,
  AddUserModalTitle,
  AddUserSelect,
  ModalCancelButton,
  ModalSubmitButton,
} from "../../style";

const { Item: FormItem, useForm } = Form;

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
}

const FULL_NAME_REGEX = /^[A-Za-z]+(?: [A-Za-z]+)*$/;

const ROLE_OPTIONS: { value: AdminUserRole; label: string }[] = [
  { value: "receptionist", label: "Receptionist" },
  { value: "admin", label: "Admin" },
];

const STATUS_OPTIONS: { value: AdminUserStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
];

export default function AddUserModal({ open, onClose }: AddUserModalProps) {
  const dispatch = useDispatch();
  const { saving, error } = useSelector(selectCreate);
  const [form] = useForm();
  const wasSaving = useRef(false);

  useEffect(() => {
    if (wasSaving.current && !saving && !error) {
      const name = (form.getFieldValue("full_name") as string) ?? "User";
      message.success(`${name} added`);
      form.resetFields();
      onClose();
    }
    wasSaving.current = saving;
  }, [saving, error, form, onClose]);

  function handleCancel() {
    form.resetFields();
    onClose();
  }

  function handleFinish(values: unknown) {
    const v = values as CreateUserPayload;
    dispatch(
      createUser({
        full_name: v.full_name.trim(),
        email: v.email.trim().toLowerCase(),
        role: v.role,
        status: v.status,
      }),
    );
  }

  return (
    <AddUserModalStyled
      open={open}
      onCancel={handleCancel}
      closable={false}
      footer={null}
      width={520}
      centered
      destroyOnHidden
    >
      <AddUserModalHeader align="center" gap={12}>
        <AddUserModalHeaderIcon align="center" justify="center">
          <UserAddOutlined />
        </AddUserModalHeaderIcon>
        <Flex vertical gap={1} flex={1}>
          <AddUserModalTitle level={4}>Add new user</AddUserModalTitle>
          <AddUserModalSubtitle>
            Create account for a receptionist or staff members
          </AddUserModalSubtitle>
        </Flex>
        <AddUserModalClose
          type="text"
          icon={<CloseOutlined />}
          onClick={handleCancel}
          aria-label="Close"
        />
      </AddUserModalHeader>
      <AddUserModalDivider />
      <AddUserModalBody>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          requiredMark={false}
        >
          <FormItem
            name="full_name"
            label="User Name"
            validateFirst
            rules={[
              { required: true, message: "Please enter a name" },
              { min: 3, message: "Name must be at least 3 characters" },
              { max: 55, message: "Name must be 55 characters or less" },
              {
                pattern: FULL_NAME_REGEX,
                message: "Name must contain only letters and spaces",
              },
            ]}
          >
            <AddUserInput placeholder="Enter name" autoComplete="off" />
          </FormItem>

          <FormItem
            name="email"
            label="Email"
            validateFirst
            rules={[
              { required: true, message: "Please enter an email" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <AddUserInput placeholder="Enter email" autoComplete="off" />
          </FormItem>

          <AddUserFieldGrid>
            <FormItem
              name="role"
              label="Select Role"
              rules={[{ required: true, message: "Please select a role" }]}
            >
              <AddUserSelect placeholder="Enter role" options={ROLE_OPTIONS} />
            </FormItem>
            <FormItem
              name="status"
              label="Initial Status"
              rules={[{ required: true, message: "Please select a status" }]}
            >
              <AddUserSelect
                placeholder="Enter initial status"
                options={STATUS_OPTIONS}
              />
            </FormItem>
          </AddUserFieldGrid>
        </Form>
      </AddUserModalBody>
      <AddUserModalFooter justify="flex-end" gap={10}>
        <ModalCancelButton onClick={handleCancel} disabled={saving}>
          Cancel
        </ModalCancelButton>
        <ModalSubmitButton onClick={() => form.submit()} loading={saving}>
          Add User
        </ModalSubmitButton>
      </AddUserModalFooter>
    </AddUserModalStyled>
  );
}
